import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import useOffer from 'hooks/Offers/useOffer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import { HotelSummaryInclusions } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryInclusions'
import Action from '../../Chat/MessageMeta/Action'
import LesaPurchaseSummaryMetaContainer from './Common/LesaPurchaseSummaryMetaContainer'
import { filterActions } from '../../../utils/actionsUtils'
import LesaHotelItemSummary from 'supportAssistant/components/SelectPurchase/LesaPurchaseSummary/LesaHotelItemSummary'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import LesaHotelPurchases from './LesaHotelPurchases'
import Group from 'components/utils/Group'
import { getHotelItems } from 'supportAssistant/utils/orderUtils'
import { INCLUSIONS_TITLE_BONUS } from 'constants/inclusions'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { CONTENT_TYPES_LIST_ORDERS } from 'supportAssistant/constants/supportAssistant'

const LesaHotelItemSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function LesaHotelPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, isCurrentOrder, canEdit } = props
  const intl = useIntl()

  const [offer] = useOffer<App.Offer>(order.items[0]?.offerId)
  const items = getHotelItems(order)
  const item = items[0]

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasInclusions = !!(item?.inclusions?.inclusions?.length || !!item?.inclusions?.inclusionHighlights?.length || !!item?.inclusions?.bonusInclusions?.length || !!item?.inclusions?.luxPlusInclusions?.length)

  const selectedOrderItem = useMemo<App.OrderItem | undefined>(() => {
    return items.find(hotelItem => hotelItem.id === payload?.itemId)
  }, [items, payload?.itemId])

  const singleRoomLabel = intl.formatMessage({
    id: 'ogl9ewzYf1',
    defaultMessage: 'Your Room',
    description: 'Label for single room in hotel purchase',
  })

  if (!offer) {
    return <LesaPurchaseSummarySkeleton />
  }

  let orderActions = actions?.filter(action => filterActions(action, isPastOrder, isTour ? ['listInclusions', 'showFinePrint'] : []))
  orderActions = orderActions?.filter(action => filterActions(action, isPastOrder, !hasInclusions ? ['listInclusions'] : []))

  const displayOrders = componentsMap?.keys().filter(key => key !== undefined).some(key => CONTENT_TYPES_LIST_ORDERS.includes(key)) || !!componentsMap?.get('selectFlightOrder') || !!componentsMap?.get('selectOrder') || !!componentsMap?.get('airportTransfersSelectOrder') || !!componentsMap?.get('selectOrderExtraGuests')

  return (
    <>
      {displayOrders && (
        <LesaHotelPurchases
          hideSelect={hideSelect}
          order={order}
          isPastOrder={isPastOrder}
          isCurrentOrder={isCurrentOrder}
          componentsMap={componentsMap}
          canEdit={canEdit}
        />
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <FinePrintModal offer={offer} />
      )}
      {!!componentsMap?.get('listInclusions') && (
        <LesaPurchaseSummaryMetaContainer>
          {!!item?.inclusions?.inclusionHighlights?.length && <>
            <BodyTextBlock variant="medium" weight="bold">{intl.formatMessage({
              id: 'l4CWQ17+2b',
              defaultMessage: 'Package inclusions:',
              description: 'Bold label for package inclusions section',
            })}
            </BodyTextBlock>
            {item.inclusions.inclusionHighlights.map(inclusion => <OfferInclusionItem
              key={inclusion.id}
              inclusion={inclusion}
            />)}
          </>}
          {!!item?.inclusions?.bonusInclusions?.length && <>
            <BodyTextBlock variant="medium" weight="bold" format="propercase">{INCLUSIONS_TITLE_BONUS}:</BodyTextBlock>
            {item.inclusions.bonusInclusions.map(inclusion => <OfferInclusionItem
              key={inclusion.id}
              inclusion={inclusion}
            />)}
          </>}
          {(!item?.inclusions?.inclusionHighlights && !item?.inclusions?.bonusInclusions.length && !!item.inclusions?.inclusions?.length) &&
            <HotelSummaryInclusions inclusions={item.inclusions.inclusions} />}
        </LesaPurchaseSummaryMetaContainer>
      )}
      {(!!componentsMap?.get('list-order-items') || !!componentsMap?.get('list-order-items-extra-guests')) && (
        <LesaHotelItemSummaryContainer>
          {items?.map((hotelItem, index) => {
            let roomLabel = singleRoomLabel
            if (items?.length !== 1) {
              roomLabel = intl.formatMessage({
                id: '0pLemobfVD',
                defaultMessage: 'Room {index}',
                description: 'Label for hotel room item',
              }, { index: index + 1 })
            }

            return <LesaHotelItemSummary
              key={index}
              item={hotelItem}
              order={order}
              offer={offer}
              roomLabel={roomLabel}
              isCurrentOrder={isCurrentOrder}
              componentsMap={componentsMap}
            />
          })}
        </LesaHotelItemSummaryContainer>
      )}
      {(!!componentsMap?.get('selectOrderItem') || !!componentsMap?.get('selectOrderItemExtraGuests')) && !!selectedOrderItem && (
        <LesaHotelItemSummaryContainer>
          <LesaHotelItemSummary
            hideSelectButton
            item={selectedOrderItem}
            order={order}
            offer={offer}
            roomLabel={intl.formatMessage({
              id: '9pUfnZGd02',
              defaultMessage: 'Selected room',
              description: 'Label for the selected room in order item view',
            })}
            componentsMap={componentsMap}
          />
        </LesaHotelItemSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions?.map((action) => (
            <Action
              key={action.action?.key}
              action={action}
              payload={payload}
              order={order}
              offer={offer}
            />
          ))}
        </Group>}
    </>
  )
}
